/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
  useState, useRef, useEffect, useCallback
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Bot, Send, ChevronDown, ChevronRight, Edit2, X, Check
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Card, CardContent, CardHeader, CardTitle, CardDescription
} from 'shared/components/ui/card';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 TablePreview
} from './TablePreview';
import {
 assistantWsService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/Assistant.websocket.service';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
 RotateCcw, ArrowLeft
} from 'lucide-react';
import {
 useNavigate
} from 'react-router-dom';

import {
 TableColumn
} from './types/builderTables.types';

interface Table {
    tableName: string;
    columns: TableColumn[];
}

interface Message {
    role: 'user' | 'assistant';
    content: string;
    table?: Table;
    isHidden?: boolean;
}

interface CreateTableAIPageProps {
    addTable: (table: { id: string; name: string; icon: string }) => void;
    fetchTables: () => void;
    addNewNotifcation: (message: string, type: 'success' | 'danger') => void;
}

function cleanJSONString(rawOutput: string): string {
    if (!rawOutput) return '';

    let cleaned = rawOutput.trim();
    if (cleaned.startsWith('```json')) {
        cleaned = cleaned.slice(7);
    } else if (cleaned.startsWith('```')) {
        cleaned = cleaned.slice(3);
    }

    if (cleaned.endsWith('```')) {
        cleaned = cleaned.slice(0, -3);
    }

    cleaned = cleaned.replace(/,\s*([\]}])/g, '$1');
    return cleaned.trim();
}

function fixJSON(jsonStr: string): string {
    let fixed = jsonStr.trim();
    if (!fixed.startsWith('{')) {
        fixed = `{${fixed}`;
    }
    if (!fixed.endsWith('}')) {
        fixed = `${fixed}}`;
    }
    return fixed;
}

export function CreateTableAIPage({
    addTable,
    fetchTables,
    addNewNotifcation,
}: CreateTableAIPageProps) {
    const {
 oragID, subOragID, appId
} = useParams();
    const navigate = useNavigate();

    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'assistant',
            content:
                "Hi there! I'm your AI table creation assistant. Describe the table you want to create, and I'll help you set it up. You can tell me about the fields you need, data types, relationships with other tables, and any constraints.",
        },
    ]);
    const [input, setInput] = useState('');
    const [collapsedTables, setCollapsedTables] = useState<{ [key: number]: boolean }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateLoading, setIsCreateLoading] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [lastEditedTableIndex, setLastEditedTableIndex] = useState<number | null>(null);
    const opcodeID = 'databuilder2';
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const editInputRef = useRef<HTMLTextAreaElement | null>(null);
    const tableRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    const toggleCollapse = (index: number) => {
        setCollapsedTables((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const scrollToTable = useCallback((index: number) => {
        if (tableRefs.current[index]) {
            tableRefs.current[index]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, []);

    useEffect(() => {
        if (lastEditedTableIndex !== null) {
            scrollToTable(lastEditedTableIndex);
        }
    }, [lastEditedTableIndex, scrollToTable]);

    useEffect(() => {
        if (editingIndex !== null) {
            editInputRef.current?.focus();
        }
    }, [editingIndex]);

    const handleSendMessage = async (messageToSend = input, editIndex: number | null = null) => {
        if (!messageToSend.trim()) return;

        setIsLoading(true);

        if (editIndex !== null) {
            setMessages((prev) => {
                const newMessages = [...prev];
                newMessages[editIndex] = {
                    ...newMessages[editIndex],
                    content: messageToSend,
                };
                return newMessages;
            });
        } else {
            setMessages((prev) => [...prev, {
 role: 'user',
content: messageToSend
}]);
        }
        setInput('');

        try {
            assistantWsService.setAssistant('68c92422d08caca4c54b28e6');
            const response = await assistantWsService.sendToAssistant(oragID, subOragID, opcodeID, {
                real_time_data: messageToSend,
            });

            const lastStepOutput = response?.result?.execution_log?.slice(-1)[0]?.Outputs
                ? Object.values(response.result.execution_log.slice(-1)[0].Outputs)[0]
                : '';

            let parsedOutput;
            try {
                parsedOutput = typeof lastStepOutput === 'string'
                    ? JSON.parse(fixJSON(cleanJSONString(lastStepOutput)))
                    : lastStepOutput;
            } catch (error) {
                console.error('Error parsing JSON:', error, '\nOutput:', lastStepOutput);
            }

            if (parsedOutput?.tables?.length > 0) {
                setMessages((prev) => {
                    const updatedMessages = prev.map((msg) => (msg.table ? {
 ...msg,
isHidden: true
} : msg));

                    let newTableIndex;
                    if (editIndex !== null) {
                        updatedMessages[editIndex + 1] = {
                            role: 'assistant',
                            content: "Here's your updated table with the requested fields:",
                            table: parsedOutput.tables[0],
                            isHidden: false,
                        };
                        newTableIndex = editIndex + 1;
                    } else {
                        newTableIndex = updatedMessages.length;
                        updatedMessages.push({
                            role: 'assistant',
                            content: "Here's your table with the requested fields:",
                            table: parsedOutput.tables[0],
                            isHidden: false,
                        });
                    }

                    setLastEditedTableIndex(newTableIndex);
                    return updatedMessages;
                });

                setCollapsedTables((prev) => {
                    const newState = {
 ...prev
};
                    Object.keys(newState).forEach((key) => {
                        newState[key] = true;
                    });
                    return newState;
                });
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: "I couldn't generate a table schema based on your input.",
                    },
                ]);
            }
        } catch (error) {
            console.error('Error while communicating with assistant:', error);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'There was an error processing your request. Please try again.',
                },
            ]);
        } finally {
            setIsLoading(false);
            setEditingIndex(null);
        }
    };

    const handleEditMessage = (index: number) => {
        setEditingIndex(index);
        setEditValue(messages[index].content);
    };

    const cancelEditing = () => {
        setEditingIndex(null);
        setEditValue('');
    };

    const saveEdit = () => {
        if (editingIndex !== null) {
            setEditingIndex(null);
            handleSendMessage(editValue, editingIndex);
        }
    };

    const handleCreateTable = async () => {
        const lastTable = messages[lastEditedTableIndex]?.table || messages.reverse().find((msg) => msg.table)?.table;
        if (!lastTable) {
            console.error('No table selected!');
            addNewNotifcation('No table selected. Please generate a table first.', 'danger');
            return;
        }

        setIsCreateLoading(true);

        const payload = {
            schemas: [
                {
                    tableName: lastTable.tableName,
                    columns: lastTable.columns.map((col) => ({
                        columnName: col.name,
                        dataType: col.type,
                        length: col.fieldLength ? Number.parseInt(String(col.fieldLength), 10) : 10,
                        fieldsAccessBy: [col.fieldsAccessBy],
                        description: col.description,
                        format: col.dateFormat || '',
                        default: col.defaultValue || '',
                        constraints: col.constraints?.length > 0 ? col.constraints : [''],
                        references: {
                            type: col.relationshipTypes || '',
                            table: col.foreignKeyTable || '',
                            column: '',
                        },
                        values: col.values?.length > 0 ? col.values : [''],
                    })),
                },
            ],
        };

        try {

            await schemaService.createSchemas(oragID, appId, payload);

            addNewNotifcation('Table Created Successfully', 'success');

            const newTable = {
                id: Date.now().toString(),
                name: lastTable.tableName.trim(),
                icon: 'Database',
            };
            addTable(newTable);

            localStorage.setItem('selectedTable', newTable.name);
            const newPath = location.pathname.replace(/\/create-table$/, '');
            navigate(newPath);

            await fetchTables();
        } catch (err) {
            console.error('Error creating schema:', err);
            addNewNotifcation('Error creating table, no tables were generated.', 'danger');
        }

        setIsCreateLoading(false);
    };

    const handleReset = () => {
        setMessages([
            {
                role: 'assistant',
                content: "Hello! I'm here to help you create new tables. Let me know if there is something I can help you with regarding Schema generation.",
            },
        ]);
        setLastEditedTableIndex(null);
    };

    return (
        <div className="space-y-6 overflow-y-scroll p-2 rounded-[10px]">
            <div className="p-4 border-b">
                <Button variant="ghost" className="gap-2" onClick={() => navigate('../../tables')}>
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                </Button>
            </div>
            <Card className="rounded-none border-x-0 border-t-0 shadow-none flex flex-col">
                <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 border-b px-4 py-3 mb-4">
                    <CardTitle className="flex flex-row items-center gap-2 text-lg font-semibold">
                        <Bot className="h-5 w-5" /> AI Assisted Data Table Creation
                    </CardTitle>
                    <CardDescription>Describe the table you want to create and our AI will help you set it up</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col space-y-3 max-h-[500px] min-h-fit overflow-y-scroll pb-4">
                        {messages.map((msg, index) => (
                            <div key={index} className="space-y-2">
                                {/* Chat Message */}
                                {!msg.isHidden && (
                                    <div className={`flex items-start ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                                        <div
                                            className={`p-3 text-sm rounded-lg max-w-[70%] relative ${msg.role === 'assistant'
                                                ? 'bg-gray-100 text-gray-600 w-fit'
                                                : editingIndex === index
                                                    ? 'bg-black text-white w-full'
                                                    : 'bg-black text-white w-fit'
                                                }`}
                                        >
                                            {msg.role === 'user' && editingIndex === index ? (
                                                <div className="w-full">
                                                    <Textarea
                                                        ref={editInputRef}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onInput={(e) => {
                                                            const target = e.target as HTMLTextAreaElement;
                                                            target.style.height = 'auto'; // Reset height to recalculate
                                                            target.style.height = `${target.scrollHeight}px`; // Set height based on scroll height
                                                        }}
                                                        className="w-full p-2 text-gray-800 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                                                    />
                                                    <div className="flex justify-end gap-2 mt-2">
                                                        <Button
                                                            onClick={cancelEditing}
                                                            className="p-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                            aria-label="Cancel editing"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            onClick={saveEdit}
                                                            className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                                                            aria-label="Save changes"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="pr-7">{msg.content}</div>
                                                    {msg.role === 'user' && (
                                                        <Button
                                                            onClick={() => handleEditMessage(index)}
                                                            className="absolute top-2 right-2 p-1.5 bg-gray-700 text-white rounded-full hover:bg-gray-600 w-fit h-fit"
                                                            aria-label="Edit message"
                                                        >
                                                            <Edit2 className="w-3 h-3" />
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Table Preview */}
                                {msg.table && (
                                    lastEditedTableIndex === index ? (
                                        <div ref={(el) => { tableRefs.current[index] = el; }}>
                                            <TablePreview
                                                columns={msg.table.columns}
                                                tableName={msg.table.tableName}
                                                editable={false}
                                                editColumn={undefined}
                                                deleteColumn={undefined}
                                            />
                                        </div>
                                    ) : (
                                        <div ref={(el) => { tableRefs.current[index] = el; }}
                                            className="mt-2 p-4 rounded-lg border border-gray-200 transition-all max-w-[70%] bg-gray-50"
                                        >
                                            <Button
                                                className="flex items-center gap-2 text-sm text-black bg-transparent hover:bg-gray-200 shadow-none"
                                                onClick={() => toggleCollapse(index)}
                                            >
                                                {collapsedTables[index] ? <ChevronRight /> : <ChevronDown />}
                                                View previous table
                                            </Button>
                                            {!collapsedTables[index] && (
                                                <TablePreview
                                                    columns={msg.table.columns}
                                                    tableName={msg.table.tableName}
                                                    editable={false}
                                                    editColumn={undefined}
                                                    deleteColumn={undefined}
                                                />
                                            )}
                                        </div>
                                    )
                                )}

                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex items-center space-x-2 text-gray-500">
                                <span className="animate-pulse">●</span>
                                <span className="animate-pulse">●</span>
                                <span className="animate-pulse">●</span>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="mt-4 flex items-center border border-gray-300 rounded-lg p-2 bg-gray-50">
                        <Textarea
                            className="w-full border-none bg-transparent focus:ring-0 resize-none text-sm"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Describe the table you want to create..."
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !isLoading) {
                                    e.preventDefault();
                                    handleSendMessage(input);
                                }
                            }}
                            disabled={isLoading}
                        />
                        <Button onClick={() => handleSendMessage(input)} size="icon" className="ml-2" disabled={isLoading}>
                            <Send className="h-4 w-4 text-white" />
                        </Button>
                        <Button onClick={handleReset} size="icon" className="ml-2 bg-transparent hover:bg-gray-200 shadow-none">
                            <RotateCcw className="h-4 w-4 text-black" />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Button onClick={handleCreateTable} disabled={isCreateLoading} className="flex m-4 justify-self-end">
                {isCreateLoading ? 'Creating...' : 'Create Table'}
            </Button>
        </div>
    );
}
